/**
 * Downloads the client-facing medical PA orders CSV for the current worklist filters.
 *
 * @module useExportForClient
 */
import { useCallback, useState } from "react";
import { exportForClient } from "../api/postgreSQL/postCalls/exportForClient";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { downloadCsv } from "../utils/downloadCsv";

const CLIENT_EXPORT_FILENAME = "medical-pa-orders-client-export.csv";

/**
 * Fetches the client export CSV and starts a browser download.
 *
 * The CSV is saved as returned. The client endpoint already formats
 * patient name and dates, so the worklist export transform is not applied.
 *
 * @returns `exportClientCsv` to start a download, plus `loading` and `error`.
 */
export const useExportForClient = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const exportClientCsv = useCallback(
    async (params: SearchForMedicalPaOrdersFromPostgresParams) => {
      setLoading(true);
      setError(null);

      try {
        const csvContent = await exportForClient(params);
        downloadCsv(csvContent, CLIENT_EXPORT_FILENAME);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { exportClientCsv, loading, error };
};
